import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Search, Mail, Phone, Eye, MessageCircle, Filter, Archive, RotateCcw, AlertTriangle, Plus, Upload, ArrowUpDown, CalendarCheck, ChevronRight, Trash2, UserCheck, FileText, Download } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { DataCard } from '../components/common/DataCard';
import { StatsCard } from '../components/common/StatsCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useApp } from '../contexts/AppContext';
import api from '../services/api';
import { toast } from 'sonner';

const allClasses = ['Nursery','LKG','UKG','Preparatory','1','2','3','4','5','6','7','8','9','10','11','12'];
const allSections = ['A','B','C','D','E'];

function getInitials(name) {
  return name?.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) || 'ST';
}

export function StudentsPage() {
  const navigate = useNavigate();
  const { isAdmin } = useApp();
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [classFilter, setClassFilter] = useState('all');
  const [sectionFilter, setSectionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState(null);
  const [attPeriod, setAttPeriod] = useState('month');
  const [attPercentage, setAttPercentage] = useState(null);

  // Sort state
  const [sortField, setSortField] = useState('fullName');
  const [sortDir, setSortDir] = useState('asc');

  // Email modal
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // Contact modal
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [contactTarget, setContactTarget] = useState(null);

  // Archive state
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiveReason, setArchiveReason] = useState('');

  // Add Student
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    fullName: '', dateOfBirth: '', gender: 'Male', class: '', section: '', rollNumber: '',
    parentName: '', parentRelation: '', contactNumber: '', email: '', address: '',
    medicalConditions: '', admissionDate: new Date().toISOString().split('T')[0],
    bloodGroup: '', house: '', stream: '', studentSince: new Date().toISOString().split('T')[0],
    secondaryContactName: '', secondaryContactNumber: '', secondaryContactRelation: '',
  });
  const [addErrors, setAddErrors] = useState({});
  const [isAdding, setIsAdding] = useState(false);

  // Bulk Upload
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // Tab: students vs admissions
  const [activeTab, setActiveTab] = useState('students');

  // Admission Pipeline
  const [admissions, setAdmissions] = useState([]);
  const [admLoading, setAdmLoading] = useState(false);
  const [admStageFilter, setAdmStageFilter] = useState('all');
  const [isAdmOpen, setIsAdmOpen] = useState(false);
  const [admForm, setAdmForm] = useState({ fullName: '', classApplied: '', parentName: '', contactNumber: '', email: '', notes: '', stage: 'enquiry' });
  const [admSaving, setAdmSaving] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (isAdmin) loadAttPercentage();
  }, [attPeriod]);

  const loadAttPercentage = async () => {
    try {
      const data = await api.getAttendancePercentage(attPeriod);
      setAttPercentage(data);
    } catch (e) { console.error(e); }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [studentsData, statsData] = await Promise.all([
        api.listStudents(),
        api.getDashboardStats().catch(() => null),
      ]);
      setStudents(studentsData);
      setStats(statsData);
    } catch (error) {
      toast.error('Failed to load students');
    } finally {
      setIsLoading(false);
    }
  };

  // Admission pipeline
  const getToken = () => typeof window !== 'undefined' ? localStorage.getItem('sip_token') : '';
  const fetchAdmissions = async () => {
    const token = getToken();
    if (!token) {
      console.log('No token available for admissions fetch');
      return;
    }
    setAdmLoading(true);
    try {
      const qs = admStageFilter !== 'all' ? `?stage=${admStageFilter}` : '';
      const res = await fetch(`${API_URL}/api/admissions${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      setAdmissions(data.admissions || []);
    } catch (e) { 
      console.error('Failed to load admissions:', e);
      toast.error('Failed to load admissions'); 
    }
    setAdmLoading(false);
  };
  useEffect(() => { if (activeTab === 'admissions' && isAdmin) fetchAdmissions(); }, [activeTab, admStageFilter]);

  const handleCreateAdmission = async () => {
    if (!admForm.fullName || !admForm.parentName || !admForm.contactNumber) { toast.error('Name, Parent, and Contact are required'); return; }
    setAdmSaving(true);
    try {
      await fetch(`${API_URL}/api/admissions`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` }, body: JSON.stringify(admForm) });
      toast.success('Admission enquiry created');
      setIsAdmOpen(false);
      setAdmForm({ fullName: '', classApplied: '', parentName: '', contactNumber: '', email: '', notes: '', stage: 'enquiry' });
      fetchAdmissions();
    } catch { toast.error('Failed to create admission'); }
    setAdmSaving(false);
  };

  const moveAdmissionStage = async (admissionId, newStage) => {
    try {
      await fetch(`${API_URL}/api/admissions/${admissionId}/stage`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` }, body: JSON.stringify({ stage: newStage }) });
      toast.success(`Moved to ${newStage}`);
      fetchAdmissions();
      if (newStage === 'admitted') fetchData(); // Refresh student list
    } catch { toast.error('Failed to update stage'); }
  };

  const deleteAdmission = async (admissionId) => {
    try {
      await fetch(`${API_URL}/api/admissions/${admissionId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success('Deleted');
      fetchAdmissions();
    } catch { toast.error('Failed to delete'); }
  };

  const stageOrder = ['enquiry', 'registration', 'application', 'admitted', 'rejected'];
  const nextStage = (current) => {
    const idx = stageOrder.indexOf(current);
    return idx >= 0 && idx < 3 ? stageOrder[idx + 1] : null;
  };
  const stageColor = (s) => {
    if (s === 'enquiry') return 'bg-blue-500/10 text-blue-600';
    if (s === 'registration') return 'bg-amber-500/10 text-amber-600';
    if (s === 'application') return 'bg-purple-500/10 text-purple-600';
    if (s === 'admitted') return 'bg-emerald-500/10 text-emerald-600';
    if (s === 'rejected') return 'bg-red-500/10 text-red-600';
    return 'bg-slate-200 text-slate-600';
  };

  const filteredStudents = useMemo(() => {
    let list = students.filter((s) => {
      const matchSearch = s.fullName.toLowerCase().includes(searchTerm.toLowerCase());
      const matchClass = classFilter === 'all' || s.studentClass === classFilter;
      const matchSection = sectionFilter === 'all' || s.section === sectionFilter;
      const matchStatus = statusFilter === 'all' || s.status === statusFilter;
      return matchSearch && matchClass && matchSection && matchStatus;
    });
    // Sort
    list.sort((a, b) => {
      let valA, valB;
      const classOrder = {'Nursery':0,'LKG':1,'UKG':2,'Preparatory':3};
      if (sortField === 'fullName') { valA = a.fullName.toLowerCase(); valB = b.fullName.toLowerCase(); }
      else if (sortField === 'studentClass') {
        const numA = classOrder[a.studentClass] ?? (parseInt(a.studentClass) + 10 || 99);
        const numB = classOrder[b.studentClass] ?? (parseInt(b.studentClass) + 10 || 99);
        valA = numA * 100 + (a.section || '').charCodeAt(0);
        valB = numB * 100 + (b.section || '').charCodeAt(0);
      }
      else if (sortField === 'attendancePercentage') { valA = a.attendancePercentage || 0; valB = b.attendancePercentage || 0; }
      else { valA = a[sortField]; valB = b[sortField]; }
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [students, searchTerm, classFilter, sectionFilter, statusFilter, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  // Derive unique classes and sections from data
  const availableClasses = useMemo(() => [...new Set(students.map(s => s.studentClass))].sort(), [students]);
  const availableSections = useMemo(() => [...new Set(students.map(s => s.section))].sort(), [students]);

  const liveStats = useMemo(() => {
    const activeCount = students.filter(s => s.status === 'active').length;
    const archivedCount = students.filter(s => s.status === 'archived').length;
    const alumniCount = students.filter(s => s.status === 'alumni').length;
    return {
      total: activeCount,
      archivedCount,
      alumniCount,
      avgAttendance: attPercentage ? attPercentage.percentage : (students.length > 0
        ? Math.round(students.reduce((acc, s) => acc + (s.attendancePercentage || 0), 0) / students.length)
        : 0),
      filteredCount: filteredStudents.length,
    };
  }, [students, attPercentage, filteredStudents]);

  // Send Email
  const openEmailModal = (student) => {
    setEmailTarget(student);
    setEmailSubject('');
    setEmailBody('');
    setIsEmailOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailSubject || !emailBody) {
      toast.error('Please fill subject and body');
      return;
    }
    setIsSendingEmail(true);
    try {
      await api.sendStudentEmail(emailTarget.studentId, emailSubject, emailBody);
      toast.success(`Email sent for ${emailTarget.fullName}`);
      setIsEmailOpen(false);
    } catch (error) {
      toast.error(error.message || 'Failed to send email');
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Contact Parent
  const openContactModal = (student) => {
    setContactTarget(student);
    setIsContactOpen(true);
  };

  const openWhatsApp = (number, studentName) => {
    const phone = number?.replace(/[^0-9]/g, '') || '';
    if (!phone) {
      toast.error('No phone number available');
      return;
    }
    const phoneNumber = phone.startsWith('91') ? phone : `91${phone}`;
    const message = encodeURIComponent(`Hello, this is regarding your child ${studentName}.`);
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  const openArchiveDialog = (student) => {
    setArchiveTarget(student);
    setArchiveReason('');
    setIsArchiveDialogOpen(true);
  };

  const handleArchiveStudent = async () => {
    try {
      await api.archiveStudent(archiveTarget.studentId, archiveReason);
      setStudents(prev => prev.map(s =>
        s.studentId === archiveTarget.studentId ? { ...s, status: 'archived' } : s
      ));
      setIsArchiveDialogOpen(false);
      toast.success('Student archived');
    } catch (error) {
      toast.error(error.message || 'Failed to archive student');
    }
  };

  const handleRestoreStudent = async (student) => {
    try {
      await api.restoreStudent(student.studentId);
      setStudents(prev => prev.map(s =>
        s.studentId === student.studentId ? { ...s, status: 'active' } : s
      ));
      toast.success('Student restored');
    } catch (error) {
      toast.error(error.message || 'Failed to restore student');
    }
  };

  const validateAddForm = () => {
    const errors = {};
    if (!addForm.fullName.trim()) errors.fullName = 'Required';
    else if (/\d/.test(addForm.fullName)) errors.fullName = 'Name cannot contain numbers';
    if (!addForm.dateOfBirth) errors.dateOfBirth = 'Required';
    if (!addForm.gender) errors.gender = 'Required';
    if (!addForm.class) errors.class = 'Required';
    if (!addForm.section) errors.section = 'Required';
    if (!addForm.contactNumber.trim()) errors.contactNumber = 'Required';
    else if (!/^\d{10,15}$/.test(addForm.contactNumber.replace(/[^0-9]/g, ''))) errors.contactNumber = 'Invalid phone number';
    if (!addForm.parentName.trim()) errors.parentName = 'Required';
    if (addForm.parentName && /\d/.test(addForm.parentName)) errors.parentName = 'Name cannot contain numbers';
    if (!addForm.parentRelation.trim()) errors.parentRelation = 'Required';
    if (!addForm.admissionDate) errors.admissionDate = 'Required';
    if (addForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addForm.email)) errors.email = 'Invalid email';
    if (addForm.secondaryContactNumber && !/^\d{10,15}$/.test(addForm.secondaryContactNumber.replace(/[^0-9]/g, ''))) errors.secondaryContactNumber = 'Invalid phone number';
    setAddErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddStudent = async () => {
    if (!validateAddForm()) {
      toast.error('Please fix the highlighted errors');
      return;
    }
    setIsAdding(true);
    try {
      await api.createStudent({
        ...addForm,
        rollNumber: addForm.rollNumber || undefined,
      });
      toast.success('Student added successfully');
      setIsAddOpen(false);
      setAddForm({
        fullName: '', dateOfBirth: '', gender: 'Male', class: '', section: '', rollNumber: '',
        parentName: '', parentRelation: '', contactNumber: '', email: '', address: '',
        medicalConditions: '', admissionDate: new Date().toISOString().split('T')[0],
        bloodGroup: '', house: '', stream: '', studentSince: new Date().toISOString().split('T')[0],
        secondaryContactName: '', secondaryContactNumber: '', secondaryContactRelation: '',
      });
      setAddErrors({});
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to add student');
    } finally {
      setIsAdding(false);
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) { toast.error('Please select an Excel file'); return; }
    const ext = bulkFile.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls'].includes(ext)) { toast.error('Only .xlsx or .xls files are accepted'); return; }
    setIsUploading(true);
    setBulkResult(null);
    try {
      const result = await api.bulkUploadStudents(bulkFile);
      setBulkResult(result);
      if (result.created > 0) toast.success(`${result.created} students uploaded successfully`);
      if (result.errors?.length > 0) toast.error(`${result.errors.length} rows had errors`);
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Bulk upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div data-testid="students-page" className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <PageHeader
          title="Students"
          description="Manage student records, admissions, and academic progress."
        />
        {isAdmin && (
          <div className="flex gap-2">
            {activeTab === 'students' && (
              <>
                <Button onClick={() => setIsAddOpen(true)} className="bg-brand-deep-blue hover:bg-brand-deep-blue/90" data-testid="add-student-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Student
                </Button>
                <Button variant="outline" onClick={() => { setIsBulkOpen(true); setBulkResult(null); setBulkFile(null); }} data-testid="bulk-upload-btn">
                  <Upload className="w-4 h-4 mr-2" /> Bulk Upload
                </Button>
              </>
            )}
            {activeTab === 'admissions' && (
              <Button onClick={() => setIsAdmOpen(true)} className="bg-brand-deep-blue hover:bg-brand-deep-blue/90" data-testid="add-admission-btn">
                <Plus className="w-4 h-4 mr-2" /> New Enquiry
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Tab Switcher */}
      {isAdmin && (
        <div className="flex gap-1 p-1 rounded-lg bg-slate-100 dark:bg-slate-800/50 w-fit" data-testid="student-tabs">
          {[{id: 'students', label: 'Students'}, {id: 'admissions', label: 'Admission Pipeline'}].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${activeTab === tab.id ? 'bg-white dark:bg-slate-700 shadow-sm text-brand-deep-blue' : 'text-muted-foreground hover:text-foreground'}`} data-testid={`tab-${tab.id}`}>
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* ========== ADMISSION PIPELINE TAB ========== */}
      {activeTab === 'admissions' && isAdmin && (
        <div className="space-y-4">
          {/* Stage filters */}
          <div className="flex flex-wrap gap-2">
            {['all', ...stageOrder].map(s => (
              <button key={s} onClick={() => setAdmStageFilter(s)} className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${admStageFilter === s ? 'bg-brand-deep-blue text-white border-brand-deep-blue' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-brand-deep-blue'}`} data-testid={`adm-filter-${s}`}>
                {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)} {s !== 'all' && `(${admissions.filter(a => s === 'all' || a.stage === s).length})`}
              </button>
            ))}
          </div>

          {/* Admission table */}
          <DataCard>
            {admLoading ? (
              <div className="flex justify-center py-8"><div className="w-8 h-8 border-4 border-brand-light-blue border-t-transparent rounded-full animate-spin" /></div>
            ) : admissions.length === 0 ? (
              <p className="text-center py-8 text-sm text-muted-foreground">No admissions found. Create a new enquiry to start.</p>
            ) : (
              <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-800/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Student</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Class</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Parent</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Parent ID</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Stage</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                    {admissions.map(adm => (
                      <tr key={adm.admissionId} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium">{adm.fullName}</p>
                          <p className="text-xs text-muted-foreground">{adm.contactNumber}</p>
                        </td>
                        <td className="px-4 py-3 text-sm">{adm.classApplied || '-'}</td>
                        <td className="px-4 py-3 text-sm">{adm.parentName}</td>
                        <td className="px-4 py-3"><Badge variant="outline" className="text-xs font-mono">{adm.parentId || '-'}</Badge></td>
                        <td className="px-4 py-3"><Badge className={stageColor(adm.stage)}>{adm.stage}</Badge></td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">{adm.createdAt?.substring(0, 10)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            {nextStage(adm.stage) && (
                              <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => moveAdmissionStage(adm.admissionId, nextStage(adm.stage))} data-testid={`adm-advance-${adm.admissionId}`}>
                                <ChevronRight className="w-3 h-3 mr-0.5" /> {nextStage(adm.stage)}
                              </Button>
                            )}
                            {adm.stage === 'application' && (
                              <Button size="sm" className="text-xs h-7 bg-emerald-600 hover:bg-emerald-700" onClick={() => moveAdmissionStage(adm.admissionId, 'admitted')} data-testid={`adm-admit-${adm.admissionId}`}>
                                <UserCheck className="w-3 h-3 mr-0.5" /> Admit
                              </Button>
                            )}
                            {!['admitted', 'rejected'].includes(adm.stage) && (
                              <Button size="sm" variant="outline" className="text-xs h-7 text-red-500 hover:text-red-600" onClick={() => moveAdmissionStage(adm.admissionId, 'rejected')} data-testid={`adm-reject-${adm.admissionId}`}>
                                Reject
                              </Button>
                            )}
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => deleteAdmission(adm.admissionId)} data-testid={`adm-delete-${adm.admissionId}`}>
                              <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </DataCard>

          {/* New Admission Dialog */}
          <Dialog open={isAdmOpen} onOpenChange={setIsAdmOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>New Admission Enquiry</DialogTitle>
                <DialogDescription>Create a new admission pipeline entry</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <div><Label>Student Name *</Label><Input value={admForm.fullName} onChange={e => setAdmForm({...admForm, fullName: e.target.value})} data-testid="adm-name" /></div>
                <div><Label>Class Applied</Label>
                  <Select value={admForm.classApplied} onValueChange={v => setAdmForm({...admForm, classApplied: v})}>
                    <SelectTrigger data-testid="adm-class"><SelectValue placeholder="Select class" /></SelectTrigger>
                    <SelectContent>{allClasses.map(c => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Parent Name *</Label><Input value={admForm.parentName} onChange={e => setAdmForm({...admForm, parentName: e.target.value})} data-testid="adm-parent" /></div>
                <div><Label>Contact Number *</Label><Input value={admForm.contactNumber} onChange={e => setAdmForm({...admForm, contactNumber: e.target.value})} data-testid="adm-contact" /></div>
                <div><Label>Email</Label><Input value={admForm.email} onChange={e => setAdmForm({...admForm, email: e.target.value})} data-testid="adm-email" /></div>
                <div><Label>Notes</Label><Textarea value={admForm.notes} onChange={e => setAdmForm({...admForm, notes: e.target.value})} rows={2} data-testid="adm-notes" /></div>
              </div>
              <DialogFooter className="mt-3">
                <Button variant="outline" onClick={() => setIsAdmOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateAdmission} disabled={admSaving} className="bg-brand-deep-blue" data-testid="adm-submit">
                  {admSaving ? 'Creating...' : 'Create Enquiry'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* ========== STUDENTS TAB (existing) ========== */}
      {activeTab === 'students' && (
        <>

      {/* Live Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard title="Total Students" value={isLoading ? '...' : liveStats.total} description={`${liveStats.archivedCount || 0} archived, ${liveStats.alumniCount || 0} alumni`} icon={Users} delay={0} />
        <StatsCard title="Avg Attendance" value={isLoading ? '...' : `${liveStats.avgAttendance}%`} description="student attendance" delay={0.1}
          filterSlot={
            <TooltipProvider><Tooltip><TooltipTrigger asChild>
              <div onClick={(e) => e.stopPropagation()}>
                <Select value={attPeriod} onValueChange={setAttPeriod}>
                  <SelectTrigger className="h-7 text-xs w-[120px]" data-testid="students-att-period"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="week">This Week</SelectItem>
                    <SelectItem value="month">This Month</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </TooltipTrigger><TooltipContent><p>Student attendance percentage (excludes staff)</p></TooltipContent></Tooltip></TooltipProvider>
          }
        />
        <StatsCard title="Classes" value={isLoading ? '...' : availableClasses.length} description="active classes" delay={0.2} />
        <StatsCard title="Showing" value={isLoading ? '...' : liveStats.filteredCount} description={`of ${students.length} total records`} delay={0.3} />
      </div>

      {/* Filters & Table */}
      <DataCard delay={0.4}>
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search students..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" data-testid="student-search" />
          </div>
          <Select value={classFilter} onValueChange={(v) => { setClassFilter(v); setSectionFilter('all'); }}>
            <SelectTrigger className="w-[140px]" data-testid="grade-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Class" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Classes</SelectItem>
              {availableClasses.map(c => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sectionFilter} onValueChange={setSectionFilter}>
            <SelectTrigger className="w-[140px]" data-testid="section-filter">
              <SelectValue placeholder="Section" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sections</SelectItem>
              {availableSections.map(s => <SelectItem key={s} value={s}>Section {s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px]" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
              <SelectItem value="alumni">Alumni</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-brand-light-blue border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-800/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold cursor-pointer select-none" onClick={() => handleSort('fullName')} data-testid="sort-student">
                      <span className="flex items-center gap-1">Student <ArrowUpDown className="w-3 h-3 text-muted-foreground" /></span>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Roll No.</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold cursor-pointer select-none" onClick={() => handleSort('studentClass')} data-testid="sort-class">
                      <span className="flex items-center gap-1">Class <ArrowUpDown className="w-3 h-3 text-muted-foreground" /></span>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold cursor-pointer select-none" onClick={() => handleSort('attendancePercentage')} data-testid="sort-attendance">
                      <span className="flex items-center gap-1">Attendance <ArrowUpDown className="w-3 h-3 text-muted-foreground" /></span>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {filteredStudents.map((student) => (
                    <tr key={student.studentId} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-8 h-8">
                            <AvatarFallback className="bg-brand-light-blue/10 text-brand-deep-blue text-xs font-medium">
                              {getInitials(student.fullName)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-foreground text-sm">{student.fullName}</p>
                            <p className="text-xs text-muted-foreground">{student.email || '-'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{student.rollNumber || '-'}</td>
                      <td className="px-4 py-3 text-sm">{student.studentClass}-{student.section}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${student.attendancePercentage >= 90 ? 'bg-emerald-500' : student.attendancePercentage >= 75 ? 'bg-amber-500' : 'bg-red-500'}`}
                              style={{ width: `${student.attendancePercentage}%` }}
                            />
                          </div>
                          <span className="text-sm">{student.attendancePercentage}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={student.status === 'active' ? 'bg-emerald-500/10 text-emerald-600' : student.status === 'archived' ? 'bg-amber-500/10 text-amber-600' : 'bg-slate-200 text-slate-600'}>
                          {student.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="ghost" size="icon" title="Send Email" onClick={() => openEmailModal(student)} data-testid={`email-student-${student.studentId}`}>
                            <Mail className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" title="Contact Parent" onClick={() => openContactModal(student)} data-testid={`contact-parent-${student.studentId}`}>
                            <Phone className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" title="View Profile" onClick={() => navigate(`/students/${student.studentId}`)} data-testid={`view-student-${student.studentId}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {student.status === 'active' && isAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Archive"
                              onClick={() => openArchiveDialog(student)}
                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              data-testid={`archive-student-${student.studentId}`}
                            >
                              <Archive className="w-4 h-4" />
                            </Button>
                          )}
                          {student.status === 'archived' && isAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Restore"
                              onClick={() => handleRestoreStudent(student)}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              data-testid={`restore-student-${student.studentId}`}
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredStudents.length === 0 && (
              <div className="py-12 text-center text-muted-foreground">No students found</div>
            )}
            <div className="px-4 py-2 text-xs text-muted-foreground border-t border-slate-200 dark:border-slate-800">
              Showing {filteredStudents.length} of {students.length} students
            </div>
          </div>
        )}
      </DataCard>

      {/* Send Email Modal */}
      <Dialog open={isEmailOpen} onOpenChange={setIsEmailOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5" /> Send Email</DialogTitle>
            <DialogDescription>Send email regarding {emailTarget?.fullName}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>To</Label>
              <Input value={emailTarget?.email || emailTarget?.fullName || ''} disabled className="bg-slate-50" />
            </div>
            <div>
              <Label>Subject *</Label>
              <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} placeholder="Email subject" data-testid="email-subject" />
            </div>
            <div>
              <Label>Message *</Label>
              <Textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} placeholder="Write your message..." rows={4} data-testid="email-body" />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsEmailOpen(false)}>Cancel</Button>
            <Button onClick={handleSendEmail} disabled={isSendingEmail} className="bg-brand-deep-blue hover:bg-brand-deep-blue/90" data-testid="send-email-btn">
              {isSendingEmail ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Parent / Emergency Modal */}
      <Dialog open={isContactOpen} onOpenChange={setIsContactOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Phone className="w-5 h-5" /> Call Options</DialogTitle>
            <DialogDescription>Choose who to call for {contactTarget?.fullName}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {/* Parent Contact */}
            {contactTarget?.parentName && (
              <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <p className="text-sm font-medium">{contactTarget.parentName}</p>
                    <p className="text-xs text-muted-foreground">{contactTarget.parentRelation || 'Parent/Guardian'}</p>
                  </div>
                  <Badge variant="outline" className="text-[10px]">Parent</Badge>
                </div>
                <p className="text-sm mb-2">{contactTarget?.contactNumber || 'Not available'}</p>
                <div className="flex gap-2">
                  {contactTarget?.contactNumber && (
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => window.open(`tel:${contactTarget.contactNumber}`)} data-testid="call-parent-btn">
                      <Phone className="w-3.5 h-3.5 mr-1" /> Call Parent
                    </Button>
                  )}
                  <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={() => openWhatsApp(contactTarget?.whatsappNumber || contactTarget?.contactNumber, contactTarget?.fullName)}
                    data-testid="whatsapp-parent-btn">
                    <MessageCircle className="w-3.5 h-3.5 mr-1" /> WhatsApp
                  </Button>
                </div>
              </div>
            )}

            {/* Emergency / Secondary Contact */}
            {contactTarget?.secondaryContactName && (
              <div className="p-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10">
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <p className="text-sm font-medium">{contactTarget.secondaryContactName}</p>
                    <p className="text-xs text-muted-foreground">{contactTarget.secondaryContactRelation || 'Emergency Contact'}</p>
                  </div>
                  <Badge className="bg-amber-100 text-amber-700 text-[10px]">Emergency</Badge>
                </div>
                <p className="text-sm mb-2">{contactTarget?.secondaryContactNumber || 'Not available'}</p>
                <div className="flex gap-2">
                  {contactTarget?.secondaryContactNumber && (
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => window.open(`tel:${contactTarget.secondaryContactNumber}`)} data-testid="call-emergency-btn">
                      <Phone className="w-3.5 h-3.5 mr-1" /> Call
                    </Button>
                  )}
                  {contactTarget?.secondaryContactNumber && (
                    <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => openWhatsApp(contactTarget.secondaryContactNumber, contactTarget?.fullName)}
                      data-testid="whatsapp-emergency-btn">
                      <MessageCircle className="w-3.5 h-3.5 mr-1" /> WhatsApp
                    </Button>
                  )}
                </div>
              </div>
            )}
            {!contactTarget?.secondaryContactName && (
              <p className="text-xs text-muted-foreground text-center py-2">No emergency contact on file</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <AlertDialog open={isArchiveDialogOpen} onOpenChange={setIsArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Archive Student
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will archive <strong>{archiveTarget?.fullName}</strong>'s profile.
              All their data will be preserved and can be restored later if they rejoin.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="archiveReason">Reason for archiving (optional)</Label>
            <Textarea
              id="archiveReason"
              placeholder="e.g., Family relocated, Transferred to another school..."
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              className="mt-2"
              data-testid="archive-reason-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchiveStudent}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-archive-btn"
            >
              Archive Student
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

        </>
      )}

      {/* Add Student Dialog */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5" /> Add New Student</DialogTitle>
            <DialogDescription>Fill in the student details. Fields marked with * are required.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Full Name *</Label>
                <Input value={addForm.fullName} onChange={e => { const v = e.target.value.replace(/[0-9]/g, ''); setAddForm({...addForm, fullName: v}); }} data-testid="add-fullname" className={addErrors.fullName ? 'border-red-500' : ''} />
                {addErrors.fullName && <p className="text-xs text-red-500 mt-0.5">{addErrors.fullName}</p>}
              </div>
              <div><Label>Date of Birth *</Label><Input type="date" value={addForm.dateOfBirth} onChange={e => setAddForm({...addForm, dateOfBirth: e.target.value})} data-testid="add-dob" className={addErrors.dateOfBirth ? 'border-red-500' : ''} />
                {addErrors.dateOfBirth && <p className="text-xs text-red-500 mt-0.5">{addErrors.dateOfBirth}</p>}</div>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>Gender *</Label>
                <Select value={addForm.gender} onValueChange={v => setAddForm({...addForm, gender: v})}>
                  <SelectTrigger data-testid="add-gender" className={addErrors.gender ? 'border-red-500' : ''}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Class *</Label>
                <Select value={addForm.class} onValueChange={v => setAddForm({...addForm, class: v, stream: (['11','12'].includes(v) ? addForm.stream : '')})}>
                  <SelectTrigger data-testid="add-class" className={addErrors.class ? 'border-red-500' : ''}><SelectValue placeholder="Select Class" /></SelectTrigger>
                  <SelectContent>
                    {allClasses.map(c => <SelectItem key={c} value={c}>{['Nursery','LKG','UKG','Preparatory'].includes(c) ? c : `Class ${c}`}</SelectItem>)}
                  </SelectContent>
                </Select>
                {addErrors.class && <p className="text-xs text-red-500 mt-0.5">{addErrors.class}</p>}
              </div>
              <div>
                <Label>Section *</Label>
                <Select value={addForm.section} onValueChange={v => setAddForm({...addForm, section: v})}>
                  <SelectTrigger data-testid="add-section" className={addErrors.section ? 'border-red-500' : ''}><SelectValue placeholder="Section" /></SelectTrigger>
                  <SelectContent>
                    {allSections.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                {addErrors.section && <p className="text-xs text-red-500 mt-0.5">{addErrors.section}</p>}
              </div>
              <div>
                <Label>Stream {['11','12'].includes(addForm.class) ? '*' : ''}</Label>
                <Select value={addForm.stream || 'none'} onValueChange={v => setAddForm({...addForm, stream: v === 'none' ? '' : v})} disabled={!['11','12'].includes(addForm.class)}>
                  <SelectTrigger data-testid="add-stream" className={!['11','12'].includes(addForm.class) ? 'opacity-50' : ''}><SelectValue placeholder="Stream" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-</SelectItem>
                    <SelectItem value="Science">Science</SelectItem>
                    <SelectItem value="Commerce">Commerce</SelectItem>
                    <SelectItem value="Arts">Arts</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Roll Number</Label><Input type="number" value={addForm.rollNumber} onChange={e => setAddForm({...addForm, rollNumber: e.target.value})} data-testid="add-roll" /></div>
              <div><Label>Blood Group</Label>
                <Select value={addForm.bloodGroup || 'none'} onValueChange={v => setAddForm({...addForm, bloodGroup: v === 'none' ? '' : v})}>
                  <SelectTrigger data-testid="add-blood-group"><SelectValue placeholder="Blood Group" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-</SelectItem>
                    {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(bg => <SelectItem key={bg} value={bg}>{bg}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>House</Label><Input value={addForm.house} onChange={e => setAddForm({...addForm, house: e.target.value})} placeholder="e.g., Gryffindor" data-testid="add-house" /></div>
            </div>
            <p className="text-xs font-medium text-muted-foreground border-b pb-1 pt-2">Parent / Guardian Information *</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Parent Name *</Label>
                <Input value={addForm.parentName} onChange={e => { const v = e.target.value.replace(/[0-9]/g, ''); setAddForm({...addForm, parentName: v}); }} data-testid="add-parent-name" className={addErrors.parentName ? 'border-red-500' : ''} />
                {addErrors.parentName && <p className="text-xs text-red-500 mt-0.5">{addErrors.parentName}</p>}
              </div>
              <div>
                <Label>Relation *</Label>
                <Select value={addForm.parentRelation || 'none'} onValueChange={v => setAddForm({...addForm, parentRelation: v === 'none' ? '' : v})}>
                  <SelectTrigger data-testid="add-parent-relation" className={addErrors.parentRelation ? 'border-red-500' : ''}><SelectValue placeholder="Select Relation" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select...</SelectItem>
                    <SelectItem value="Father">Father</SelectItem>
                    <SelectItem value="Mother">Mother</SelectItem>
                    <SelectItem value="Guardian">Guardian</SelectItem>
                  </SelectContent>
                </Select>
                {addErrors.parentRelation && <p className="text-xs text-red-500 mt-0.5">{addErrors.parentRelation}</p>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Number *</Label>
                <Input value={addForm.contactNumber} onChange={e => { const v = e.target.value.replace(/[^0-9+\-\s]/g, ''); setAddForm({...addForm, contactNumber: v}); }} placeholder="10-digit number" data-testid="add-contact" className={addErrors.contactNumber ? 'border-red-500' : ''} />
                {addErrors.contactNumber && <p className="text-xs text-red-500 mt-0.5">{addErrors.contactNumber}</p>}
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={addForm.email} onChange={e => setAddForm({...addForm, email: e.target.value})} data-testid="add-email" className={addErrors.email ? 'border-red-500' : ''} />
                {addErrors.email && <p className="text-xs text-red-500 mt-0.5">{addErrors.email}</p>}
              </div>
            </div>
            <p className="text-xs font-medium text-muted-foreground border-b pb-1 pt-2">Secondary Contact (Optional)</p>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Name</Label><Input value={addForm.secondaryContactName} onChange={e => setAddForm({...addForm, secondaryContactName: e.target.value.replace(/[0-9]/g, '')})} data-testid="add-sec-contact-name" /></div>
              <div>
                <Label>Phone</Label>
                <Input value={addForm.secondaryContactNumber} onChange={e => setAddForm({...addForm, secondaryContactNumber: e.target.value.replace(/[^0-9+\-\s]/g, '')})} data-testid="add-sec-contact-phone" className={addErrors.secondaryContactNumber ? 'border-red-500' : ''} />
                {addErrors.secondaryContactNumber && <p className="text-xs text-red-500 mt-0.5">{addErrors.secondaryContactNumber}</p>}
              </div>
              <div><Label>Relation</Label><Input value={addForm.secondaryContactRelation} onChange={e => setAddForm({...addForm, secondaryContactRelation: e.target.value})} placeholder="e.g., Uncle" data-testid="add-sec-contact-relation" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Admission Date *</Label><Input type="date" value={addForm.admissionDate} onChange={e => setAddForm({...addForm, admissionDate: e.target.value})} data-testid="add-admission-date" className={addErrors.admissionDate ? 'border-red-500' : ''} />
                {addErrors.admissionDate && <p className="text-xs text-red-500 mt-0.5">{addErrors.admissionDate}</p>}</div>
              <div><Label>Student Since</Label><Input type="date" value={addForm.studentSince} onChange={e => setAddForm({...addForm, studentSince: e.target.value})} data-testid="add-student-since" /></div>
            </div>
            <div><Label>Address</Label><Textarea value={addForm.address} onChange={e => setAddForm({...addForm, address: e.target.value})} rows={2} data-testid="add-address" /></div>
            <div><Label>Medical Conditions</Label><Input value={addForm.medicalConditions} onChange={e => setAddForm({...addForm, medicalConditions: e.target.value})} data-testid="add-medical" /></div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAddStudent} disabled={isAdding} className="bg-brand-deep-blue hover:bg-brand-deep-blue/90" data-testid="confirm-add-student-btn">
              {isAdding ? 'Adding...' : 'Add Student'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={isBulkOpen} onOpenChange={setIsBulkOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Upload className="w-5 h-5" /> Bulk Upload Students</DialogTitle>
            <DialogDescription>
              Upload an Excel file (.xlsx) with the required columns in order:
              FullName, DateOfBirth, Gender, Class, Section, AdmissionDate, ParentName, ContactNumber, Email, Address, MedicalConditions
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <Input
              type="file"
              accept=".xlsx,.xls"
              onChange={e => setBulkFile(e.target.files[0])}
              data-testid="bulk-file-input"
            />
            {bulkResult && (
              <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800 text-sm space-y-1">
                <p className="font-medium">Upload Result:</p>
                <p className="text-emerald-600">Created: {bulkResult.created} students</p>
                {bulkResult.errors?.length > 0 && (
                  <div className="text-red-500">
                    <p>Errors: {bulkResult.errors.length} rows</p>
                    {bulkResult.errors.slice(0, 5).map((e, i) => (
                      <p key={i} className="text-xs">Row {e.rowNumber}: {e.reason}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsBulkOpen(false)}>Close</Button>
            <Button onClick={handleBulkUpload} disabled={isUploading || !bulkFile} className="bg-brand-deep-blue hover:bg-brand-deep-blue/90" data-testid="confirm-bulk-upload-btn">
              {isUploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
